import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  HiCheckCircle,
  HiXCircle,
  HiChevronDown,
  HiChevronRight,
  HiChartBar,
  HiArrowDownTray,
  HiDocumentArrowDown,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import {
  getLaunchChecklist,
  saveLaunchChecklist,
  canEdit,
  canDelete,
  deleteLaunchChecklist,
} from '../../services/commercialisationService';
import { firestore } from '../../database/firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import CommentSection from './CommentSection';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const SECTIONS = [
  {
    id: 'businessFoundations',
    title: 'Business Foundations',
    items: [
      'Business registration and licenses obtained',
      'Business bank account opened',
      'Tax identification number (TIN) registered',
      'Business insurance policies in place',
      'Operating agreements and bylaws completed',
      'Business model',
      'Financial accounts and bookkeeping system set up',
    ],
  },
  {
    id: 'productReadiness',
    title: 'Product/Service Readiness',
    items: [
      'Product/service is production-ready',
      'Quality assurance testing completed',
      'Product documentation completed',
      'User manuals and guides created',
      'Support materials prepared',
      'Product pricing finalized',
      'Packaging and branding finalized',
      'Inventory or service capacity confirmed',
    ],
  },
  {
    id: 'gtmPreparation',
    title: 'Go-To-Market Preparation',
    items: [
      'GTM strategy documented and approved',
      'Marketing materials prepared',
      'Sales collateral created',
      'Pricing strategy finalized',
      'Distribution channels established',
      'Partnership agreements signed',
      'Launch timeline and milestones defined',
      'Marketing budget allocated',
    ],
  },
  {
    id: 'brandingDigital',
    title: 'Branding & Digital Presence',
    items: [
      'Brand identity and logo finalized',
      'Website launched and tested',
      'Social media accounts set up',
      'Email marketing system configured',
      'SEO and content strategy implemented',
      'Online presence optimized',
      'Brand guidelines documented',
      'Digital assets created and organized',
    ],
  },
  {
    id: 'marketingCampaign',
    title: 'Marketing Launch Campaign',
    items: [
      'Launch campaign strategy defined',
      'Marketing channels selected',
      'Content calendar created',
      'Advertising campaigns prepared',
      'PR and media outreach planned',
      'Influencer partnerships secured',
      'Launch event planned (if applicable)',
      'Campaign tracking and analytics set up',
      'Guestlist invitations - Has the guest list been developed',
      'Guestlist invitations - Who are you targeting',
      'Guestlist invitations - The relevant sectors',
    ],
  },
  {
    id: 'launchEventBranding',
    title: 'Launch Event Marketing & Branding Materials',
    items: [
      // Event Branding & Visual Identity
      'Backdrop / Sublimated wall banner arranged for stage and photos',
      'Telescopic banners x2 prepared for venue entrance',
      'Pop-up banner x1 prepared for indoor branding and app feature highlights',
      'Branded T-shirts x20 for staff, MC, and brand ambassadors',
      'Caps x20 for staff and giveaways',
      'A5 flyers x1000 with app download QR codes and offers',
      'Business cards x150 for networking',
      // Digital & Interactive Elements
      'App demo stations/tablets set up for live demo and trial orders',
      'Large screen presentation prepared (looping video reel)',
      'QR code displays integrated on banners, flyers, and tables',
      'Videographer/photographer booked to capture event highlights',
      // Promotional & Giveaways
      'Stickers and keyholders prepared as branded merchandise',
      'Branded water bottles or cups arranged',
    ],
  },
  {
    id: 'salesAcquisition',
    title: 'Sales & Customer Acquisition',
    items: [
      'Sales process defined and documented',
      'Sales team trained (if applicable)',
      'CRM system configured',
      'Lead generation process established',
      'Customer onboarding process defined',
      'Sales collateral and pitch decks ready',
      'Pricing and quoting tools prepared',
      'Customer acquisition targets set',
    ],
  },
  {
    id: 'operationsFinance',
    title: 'Operations & Finance',
    items: [
      'Operations processes documented',
      'Supply chain or service delivery established',
      'Inventory management systems in place',
      'Customer service processes defined',
      'Financial controls and reporting systems set up',
      'Payment processing configured',
      'Accounting and bookkeeping system operational',
      'Cash flow management plan in place',
    ],
  },
  {
    id: 'teamHr',
    title: 'Team & HR',
    items: [
      'Core team roles filled',
      'Employment agreements signed',
      'HR policies and procedures documented',
      'Payroll system set up',
      'Employee benefits configured',
      'Onboarding process defined',
      'Performance management system in place',
      'Team communication tools configured',
    ],
  },
  {
    id: 'launchExecution',
    title: 'Launch Day Execution',
    items: [
      'Launch day checklist created',
      'Team roles and responsibilities assigned',
      'Customer support team ready',
      'Monitoring and tracking systems active',
      'Contingency plans prepared',
      'Launch announcement prepared',
      'Press release ready (if applicable)',
      'Launch day activities scheduled',
    ],
  },
];

const StartupLaunchChecklist = ({ userRole: propUserRole, userId: propUserId }) => {
  const { currentUser } = useAuth();
  const { userRole: contextUserRole } = useUserRole();
  const effectiveUserRole = propUserRole || contextUserRole || 'client';
  const effectiveUserId = propUserId || currentUser?.uid;

  const [formData, setFormData] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved'
  const [isCommercializedClient, setIsCommercializedClient] = useState(false);
  const [clientBusinessStage, setClientBusinessStage] = useState('');
  const [hasLaunched, setHasLaunched] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false });
  const [isDeleting, setIsDeleting] = useState(false);

  const isClientActor = (effectiveUserRole || '').toLowerCase() === 'client';
  const userCanEdit = canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId);
  const canEditChecklist = userCanEdit && !(isClientActor && hasLaunched);

  // Calculate scores
  const scores = useMemo(() => {
    const sectionScores = {};
    let totalScore = 0;
    let totalItems = 0;

    SECTIONS.forEach(section => {
      let sectionScore = 0;
      section.items.forEach((_, index) => {
        const value = formData[`${section.id}_${index}`];
        if (value === 'yes') {
          sectionScore += 1;
        } else if (value === 'partial') {
          sectionScore += 0.5;
        }
      });
      const total = section.items.length;
      const percentage = total > 0 ? Math.round((sectionScore / total) * 100) : 0;
      sectionScores[section.id] = { checked: sectionScore, total, percentage };
      totalScore += sectionScore;
      totalItems += total;
    });

    const overallScore = totalItems > 0 ? Math.round((totalScore / totalItems) * 100) : 0;

    let readinessLevel = 'Not Ready';
    let readinessColor = 'red';
    if (overallScore >= 85) {
      readinessLevel = 'Ready to Launch';
      readinessColor = 'green';
    } else if (overallScore >= 60) {
      readinessLevel = 'Almost Ready';
      readinessColor = 'yellow';
    }

    // Identify strong and weak areas
    const strongAreas = Object.entries(sectionScores)
      .filter(([_, score]) => score.percentage >= 85)
      .map(([id]) => SECTIONS.find(s => s.id === id)?.title);

    const weakAreas = Object.entries(sectionScores)
      .filter(([_, score]) => score.percentage < 60)
      .map(([id]) => SECTIONS.find(s => s.id === id)?.title);

    return {
      sectionScores,
      overallScore,
      readinessLevel,
      readinessColor,
      totalChecked: totalScore,
      totalItems,
      strongAreas,
      weakAreas,
    };
  }, [formData]);

  // Real-time listener for Launch Checklist data
  useEffect(() => {
    if (!effectiveUserId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    const docRef = doc(
      firestore,
      'commercialisation',
      effectiveUserId,
      'launchChecklist',
      effectiveUserId
    );

    const unsubscribe = onSnapshot(
      docRef,
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFormData(data.responses || {});
        }
        setLoading(false);
      },
      error => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId, currentUser?.uid]);

  useEffect(() => {
    const fetchClientStage = async () => {
      if (!effectiveUserId) return;
      try {
        const [clientSnap, profileSnap] = await Promise.all([
          getDoc(doc(firestore, 'clients', effectiveUserId)),
          getDoc(doc(firestore, 'companyProfiles', effectiveUserId)),
        ]);

        if (clientSnap.exists()) {
          const stage = clientSnap.data()?.businessStage || '';
          setClientBusinessStage(stage);
          const lowerStage = stage.toString().toLowerCase();
          const isCommercial = [
            'commercialized',
            'commercialised',
            'commercialisation',
            'commercialization',
          ].some(flag => lowerStage.includes(flag));
          setIsCommercializedClient(isCommercial);
        } else {
          setClientBusinessStage('');
          setIsCommercializedClient(false);
        }

        if (profileSnap.exists()) {
          setHasLaunched(Boolean(profileSnap.data()?.isLaunched));
        } else {
          setHasLaunched(false);
        }
      } catch (error) {
        setClientBusinessStage('');
        setIsCommercializedClient(false);
        setHasLaunched(false);
      }
    };

    fetchClientStage();
  }, [effectiveUserId]);

  // Auto-save with visual feedback
  useEffect(() => {
    if (!effectiveUserId || !currentUser?.uid || loading) return;
    if (!canEditChecklist) return;

    const autoSaveTimer = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');
        await saveLaunchChecklist(
          effectiveUserId,
          {
            responses: formData,
            score: scores.overallScore,
            readinessLevel: scores.readinessLevel,
            categoryScores: scores.sectionScores,
            totalChecked: scores.totalChecked,
            totalItems: scores.totalItems,
            strongAreas: scores.strongAreas,
            weakAreas: scores.weakAreas,
          },
          currentUser
        );
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch (error) {
        setAutoSaveStatus('idle');
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData, currentUser?.uid, loading, scores, effectiveUserId, canEditChecklist]);

  const toggleSection = sectionId => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleRadioChange = (sectionId, index, value) => {
    if (!canEditChecklist) return;
    const key = `${sectionId}_${index}`;
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in to save');
      return;
    }

    if (!canEditChecklist) {
      toast.error('Access denied');
      return;
    }

    setSaving(true);
    try {
      await saveLaunchChecklist(
        effectiveUserId,
        {
          responses: formData,
          score: scores.overallScore,
          readinessLevel: scores.readinessLevel,
          categoryScores: scores.sectionScores,
          totalChecked: scores.totalChecked,
          totalItems: scores.totalItems,
          strongAreas: scores.strongAreas,
          weakAreas: scores.weakAreas,
        },
        currentUser
      );

      toast.success('Progress saved successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in to delete');
      return;
    }

    if (!canDelete(effectiveUserRole, currentUser.uid, effectiveUserId)) {
      toast.error('Access denied');
      return;
    }

    setDeleteConfirm({ isOpen: true });
  };

  const handleDeleteConfirm = async () => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in to delete');
      return;
    }

    setIsDeleting(true);
    setSaving(true);
    try {
      await deleteLaunchChecklist(effectiveUserId, currentUser);
      setFormData({});
      toast.success('Checklist deleted successfully!');
      setDeleteConfirm({ isOpen: false });
    } catch (error) {
      toast.error(error.message || 'Failed to delete checklist');
    } finally {
      setSaving(false);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false });
  };

  const handleExport = () => {
    const data = {
      timestamp: new Date().toISOString(),
      score: scores.overallScore,
      readinessLevel: scores.readinessLevel,
      sectionScores: scores.sectionScores,
      strongAreas: scores.strongAreas,
      weakAreas: scores.weakAreas,
      responses: formData,
      report: generateLaunchReadinessReport(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `startup-launch-checklist-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Data exported successfully!');
  };

  const generateLaunchReadinessReport = () => {
    return {
      overallScore: scores.overallScore,
      readinessLevel: scores.readinessLevel,
      totalItems: scores.totalItems,
      completedItems: scores.totalChecked,
      completionRate: `${scores.overallScore}%`,
      strongAreas: scores.strongAreas,
      weakAreas: scores.weakAreas,
      sectionBreakdown: Object.entries(scores.sectionScores).map(([id, score]) => ({
        section: SECTIONS.find(s => s.id === id)?.title,
        completed: score.checked,
        total: score.total,
        percentage: score.percentage,
        status:
          score.percentage >= 85 ? 'Strong' : score.percentage >= 60 ? 'Good' : 'Needs Attention',
      })),
      recommendations: generateRecommendations(),
      generatedAt: new Date().toISOString(),
    };
  };

  const generateRecommendations = () => {
    const recommendations = [];

    if (scores.weakAreas.length > 0) {
      recommendations.push({
        priority: 'High',
        area: 'Focus Areas',
        action: `Prioritize completion of: ${scores.weakAreas.join(', ')}`,
      });
    }

    if (scores.overallScore < 60) {
      recommendations.push({
        priority: 'High',
        area: 'Overall Readiness',
        action: 'Significant progress needed before launch. Review and complete critical items.',
      });
    } else if (scores.overallScore < 85) {
      recommendations.push({
        priority: 'Medium',
        area: 'Overall Readiness',
        action: 'Continue completing remaining items to reach launch readiness.',
      });
    }

    return recommendations;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading checklist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="mb-2 text-3xl font-bold text-black">Start-Up Launch Checklist</h1>
              <p className="text-gray-600">
                Use this comprehensive checklist to ensure a successful startup launch
              </p>
              {isClientActor && (isCommercializedClient || hasLaunched) && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  <span className="block">
                    Remember: being commercialised does not automatically mean the business has
                    launched. Launch access is controlled from the company profile launch toggle.
                  </span>
                  {hasLaunched ? (
                    <span className="mt-2 block text-xs text-amber-600/90">
                      Launch status is set to <strong>Launched</strong>, so this checklist is
                      currently read-only. Update the company profile if that status changes.
                    </span>
                  ) : (
                    <span className="mt-2 block text-xs text-amber-600/80">
                      Current business stage: {clientBusinessStage || 'Commercialised'}. You can
                      keep editing until you officially mark the launch in your company profile.
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-save indicator */}
              {canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 text-sm text-gray-500"
                >
                  {autoSaveStatus === 'saving' && (
                    <>
                      <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
                      <span>Saving...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <HiCheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-green-600">Saved</span>
                    </>
                  )}
                </motion.div>
              )}
              {canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                <button
                  onClick={handleSave}
                  disabled={saving || !canEditChecklist}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
                >
                  <HiDocumentArrowDown className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
              {canDelete(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                <button
                  onClick={handleDeleteClick}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50 disabled:opacity-50"
                >
                  <HiXCircle className="h-4 w-4" />
                  Delete
                </button>
              )}
              <button
                onClick={handleExport}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
              >
                <HiArrowDownTray className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Overall Score Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Launch Readiness Score</h2>
              <p className="mt-1 text-sm text-gray-600">{scores.totalItems} items total</p>
            </div>
            <div className="text-right">
              <div
                className={`text-4xl font-bold ${
                  scores.readinessColor === 'green'
                    ? 'text-green-600'
                    : scores.readinessColor === 'yellow'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                }`}
              >
                {scores.overallScore}%
              </div>
              <div
                className={`mt-1 text-sm font-medium ${
                  scores.readinessColor === 'green'
                    ? 'text-green-700'
                    : scores.readinessColor === 'yellow'
                      ? 'text-yellow-700'
                      : 'text-red-700'
                }`}
              >
                {scores.readinessLevel}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${scores.overallScore}%` }}
                transition={{ duration: 0.5 }}
                className={`h-full ${
                  scores.readinessColor === 'green'
                    ? 'bg-green-600'
                    : scores.readinessColor === 'yellow'
                      ? 'bg-yellow-600'
                      : 'bg-red-600'
                }`}
              />
            </div>
          </div>
        </motion.div>

        {/* Launch Readiness Report */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Launch Readiness Report</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h4 className="mb-2 text-sm font-medium text-gray-700">Strong Areas</h4>
              {scores.strongAreas.length > 0 ? (
                <ul className="space-y-1">
                  {scores.strongAreas.map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-green-700">
                      <HiCheckCircle className="h-4 w-4" />
                      {area}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">None yet</p>
              )}
            </div>
            <div>
              <h4 className="mb-2 text-sm font-medium text-gray-700">Areas Needing Attention</h4>
              {scores.weakAreas.length > 0 ? (
                <ul className="space-y-1">
                  {scores.weakAreas.map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-red-700">
                      <HiXCircle className="h-4 w-4" />
                      {area}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">None</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Sections */}
        <div className="space-y-4">
          {SECTIONS.map((section, sectionIndex) => {
            const isExpanded = expandedSections[section.id];
            const sectionScore = scores.sectionScores[section.id];
            const progress = sectionScore?.percentage || 0;

            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.1 }}
                className="rounded-xl border border-gray-200 bg-white shadow-sm"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50">
                      <HiChartBar className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        {sectionScore?.total || 0} items ({progress}% complete)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">{progress}%</div>
                    </div>
                    {isExpanded ? (
                      <HiChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <HiChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Progress Bar */}
                <div className="px-6 pb-4">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full transition-all duration-300 ${
                        progress >= 85
                          ? 'bg-green-600'
                          : progress >= 60
                            ? 'bg-yellow-600'
                            : 'bg-red-600'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-gray-200 px-6 py-4"
                    >
                      <div className="space-y-3">
                        {section.items.map((item, index) => {
                          const key = `${section.id}_${index}`;
                          const currentValue = formData[key] || 'no';

                          return (
                            <div
                              key={key}
                              className={`flex flex-col gap-2 rounded-lg p-3 transition-colors ${
                                canEditChecklist
                                  ? 'hover:bg-gray-50'
                                  : 'cursor-not-allowed opacity-70'
                              }`}
                            >
                              <span className="text-sm font-medium text-gray-900">{item}</span>
                              <div className="flex items-center gap-4">
                                <label className="flex cursor-pointer items-center gap-2">
                                  <input
                                    type="radio"
                                    name={key}
                                    value="yes"
                                    checked={currentValue === 'yes'}
                                    onChange={() => handleRadioChange(section.id, index, 'yes')}
                                    className="h-4 w-4 border-gray-300 text-green-600 focus:ring-green-500"
                                    disabled={!canEditChecklist}
                                  />
                                  <span className="text-sm text-gray-700">Yes</span>
                                </label>
                                <label className="flex cursor-pointer items-center gap-2">
                                  <input
                                    type="radio"
                                    name={key}
                                    value="partial"
                                    checked={currentValue === 'partial'}
                                    onChange={() => handleRadioChange(section.id, index, 'partial')}
                                    className="h-4 w-4 border-gray-300 text-yellow-600 focus:ring-yellow-500"
                                    disabled={!canEditChecklist}
                                  />
                                  <span className="text-sm text-gray-700">Partial</span>
                                </label>
                                <label className="flex cursor-pointer items-center gap-2">
                                  <input
                                    type="radio"
                                    name={key}
                                    value="no"
                                    checked={currentValue === 'no'}
                                    onChange={() => handleRadioChange(section.id, index, 'no')}
                                    className="h-4 w-4 border-gray-300 text-red-600 focus:ring-red-500"
                                    disabled={!canEditChecklist}
                                  />
                                  <span className="text-sm text-gray-700">No</span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Section Scores Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-6"
        >
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Section Breakdown</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SECTIONS.map(section => {
              const sectionScore = scores.sectionScores[section.id];
              const progress = sectionScore?.percentage || 0;

              return (
                <div key={section.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="mb-2 text-sm font-medium text-gray-700">{section.title}</div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className={`h-full ${
                          progress >= 85
                            ? 'bg-green-600'
                            : progress >= 60
                              ? 'bg-yellow-600'
                              : 'bg-red-600'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{progress}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Comment Section */}
        {effectiveUserId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8"
          >
            <CommentSection userId={effectiveUserId} checklistType="launch" />
          </motion.div>
        )}

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Checklist"
          message="Are you sure you want to delete this checklist? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default StartupLaunchChecklist;
